import { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogContent,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { CurrencyInput } from '@/components/ui/currency-input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { X, Plus, Trash2, ChevronDown, ChevronRight, Pencil } from 'lucide-react';
import { formatCurrency } from '@/utils/formatters';
import { isTeamRopingEvent } from '@/utils/eventTypes';
import { describeBalancedSplit } from '@/utils/balancedPositions';
import { RODEO_EVENT_TYPE_NAMES } from '@/constants/event-types';
import { useStripeFeeConfig } from '@/api/extensions/useStripeFeeConfig';
import { calculateTransactionFee } from '@/utils/stripe-fee-calculator';
import { useAuthStore } from '@/state_management_and_stores/auth.store';
import { StripeFeeConfigModal } from '@/components/admin/StripeFeeConfigModal';
import { useJudgeSheetTemplates } from '@/api/extensions/useJudgeSheetTemplates';

/** Prize adjustment (add/subtract from prize pool) */
export interface PrizeAdjustment {
  id: string;
  operation: 'add' | 'subtract';
  adjustment_type: 'amount' | 'percentage';
  amount: number;
  percentage: number;
  reason: string;
  markedForDeletion?: boolean;
}

/** Prize place allocation within a round */
export interface PrizePlace {
  place: number;
  percentage: number;
  prize_amount: number;
}

/** Per-round prize configuration */
export interface RoundPrizeConfig {
  round_number: number;
  has_prizes: boolean;
  round_prize_total: number;
  places: PrizePlace[];
}

export interface EventFormData {
  id?: string;
  event_name: string;
  /**
   * Event discipline (Team Roping, Barrel Racing, etc.). Optional because
   * some legacy callers don't track it; the dialog defaults the select to
   * "Team Roping" if absent.
   */
  event_type?: string;
  entry_fee?: number;
  max_contestants?: number;
  /** Default contestants in any single performance. See JackpotEventDraft. */
  contestants_per_performance?: number;
  handicap_type?: string;
  max_combined_handicap?: number;
  header_max_handicap?: number;
  heeler_max_handicap?: number;
  max_individual_handicap?: number;
  total_rounds?: number;
  progressive_after_round?: number;
  guaranteed_runs?: number;
  is_century_jackpot?: boolean;
  is_mixed_gender?: boolean;
  // Pick and Draw
  pick_count?: number;
  draw_count?: number;
  enforce_mutual_picks?: boolean;
  // Slides
  slide_type?: string;
  slide_base_handicap?: number;
  slide_conversion_rate?: number;
  // Fees & prizes
  total_prize_amount?: number;
  // Prize allocation per round
  round_prizes?: RoundPrizeConfig[];
  // Prize adjustments
  prize_adjustments?: PrizeAdjustment[];
  // Flags
  allow_no_partner_entry?: boolean;
  /**
   * When true, the same contestant may register more than once for this
   * event up to `max_registrations_per_user`. Defaults to false (one
   * entry per contestant; team roping still gets the implicit
   * Header+Heeler exception via position).
   */
  allow_multiple_registrations?: boolean;
  /**
   * Per-contestant entry cap when multi-registration is enabled. Ignored
   * when `allow_multiple_registrations` is false. Defaults to 1.
   */
  max_registrations_per_user?: number;
  /**
   * Team roping only. When true, a contestant's entries must be split as
   * evenly as possible between Header and Heeler — at most
   * ceil(max_registrations_per_user / 2) per side. Ignored when
   * multi-registration is off or the cap is 1.
   */
  enforce_balanced_positions?: boolean;
  /**
   * FK to public.judge_sheet_templates — which printable layout the
   * secretary picked. NULL falls back to the auto-classified default
   * derived from the event discipline.
   */
  judge_sheet_template_id?: string | null;
  [key: string]: unknown;
}

interface EditEventDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  event: EventFormData | null;
  onSave: (data: EventFormData) => void;
}

const HANDICAP_TYPES = [
  { value: 'combined', label: 'Combined' },
  { value: 'individual', label: 'Individual' },
  { value: 'none', label: 'None' },
];

const SLIDE_TYPES = [
  { value: 'NONE', label: 'Disabled' },
  { value: 'SLIDE_DOWN', label: 'Slide Down Only' },
  { value: 'SLIDE_UP_DOWN', label: 'Slide Up and Down' },
];

const ROUND_OPTIONS = Array.from({ length: 10 }, (_, i) => i + 1);

const PLACE_LABELS = ['1st', '2nd', '3rd', '4th', '5th', '6th', '7th', '8th', '9th', '10th'];

/** Parse number input: returns undefined when empty, preserves 0 */
function parseNum(val: string): number | undefined {
  if (val === '') return undefined;
  const n = parseFloat(val);
  return isNaN(n) ? undefined : n;
}

/** Parse integer input: returns undefined when empty, preserves 0 */
function parseIntOpt(val: string): number | undefined {
  if (val === '') return undefined;
  const n = parseInt(val, 10);
  return isNaN(n) ? undefined : n;
}

/** Default distribution percentages by place count */
const DEFAULT_DISTRIBUTIONS: Record<number, number[]> = {
  1: [100],
  2: [60, 40],
  3: [50, 30, 20],
  4: [40, 30, 20, 10],
  5: [35, 25, 20, 12, 8],
  6: [30, 25, 20, 15, 7, 3],
  7: [25, 20, 18, 15, 12, 7, 3],
  8: [22, 18, 16, 14, 12, 8, 6, 4],
  9: [20, 16, 14, 12, 10, 8, 6, 4, 2],
  10: [18, 15, 13, 11, 9, 7, 5, 4, 3, 2],
};

/** Create default places for a prize entry */
function createDefaultPlaces(count: number, prizeAmount: number = 0): PrizePlace[] {
  const pcts = DEFAULT_DISTRIBUTIONS[count] ?? Array.from({ length: count }, () => Math.floor(100 / count));
  return pcts.map((p, i) => ({
    place: i + 1,
    percentage: p,
    prize_amount: prizeAmount > 0 ? Math.round((prizeAmount * p) / 100) : 0,
  }));
}

/** Format round display label — 0 = AVG */
function formatRoundDisplay(round: number): string {
  if (round === 0) return 'AVG';
  return `Round ${round}`;
}

/**
 * Event discipline options shown in the Event Type select. Sourced from
 * the canonical `RODEO_EVENT_TYPES` list (base disciplines first, novice
 * duplicates appended) plus an `Other` escape-hatch for jackpots that
 * don't fit the rodeo taxonomy. The wizard-embedded and standalone edit
 * paths both render this exact list.
 */
export const EVENT_TYPE_OPTIONS = [...RODEO_EVENT_TYPE_NAMES, 'Other'];

const formDefaults: EventFormData = {
  event_name: '',
  event_type: 'Team Roping',
  entry_fee: undefined,
  max_contestants: undefined,
  contestants_per_performance: undefined,
  handicap_type: 'combined',
  max_combined_handicap: undefined,
  header_max_handicap: undefined,
  heeler_max_handicap: undefined,
  max_individual_handicap: undefined,
  total_rounds: 1,
  progressive_after_round: undefined,
  guaranteed_runs: 1,
  is_century_jackpot: false,
  is_mixed_gender: false,
  pick_count: undefined,
  draw_count: undefined,
  enforce_mutual_picks: true,
  slide_type: 'NONE',
  slide_base_handicap: undefined,
  slide_conversion_rate: undefined,
  total_prize_amount: undefined,
  round_prizes: undefined,
  allow_no_partner_entry: false,
  allow_multiple_registrations: false,
  max_registrations_per_user: undefined,
  enforce_balanced_positions: false,
};

function InlineCheckbox({
  label,
  checked,
  onChange,
}: {
  label: string;
  checked: boolean;
  onChange: (v: boolean) => void;
}) {
  return (
    <label className="flex items-center gap-2 text-sm cursor-pointer">
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        className="h-4 w-4 rounded border-gray-300"
      />
      {label}
    </label>
  );
}

/** Section header consistent with old modal */
function SectionTitle({ children }: { children: React.ReactNode }) {
  return (
    <h3 className="text-lg font-semibold text-foreground">{children}</h3>
  );
}

/** Sub-section header */
function SubSectionTitle({ children }: { children: React.ReactNode }) {
  return (
    <h4 className="text-sm font-semibold text-foreground">{children}</h4>
  );
}

/** Single prize entry panel — shows all places with sliders simultaneously */
function PrizeEntryPanel({
  config,
  totalRounds,
  usedRounds,
  onChange,
  onRemove,
}: {
  config: RoundPrizeConfig;
  totalRounds: number;
  usedRounds: number[];
  onChange: (updated: RoundPrizeConfig) => void;
  onRemove: () => void;
}) {
  const totalPct = config.places.reduce((s, p) => s + p.percentage, 0);
  const isComplete = Math.abs(totalPct - 100) < 0.5;

  const [collapsed, setCollapsed] = useState(false);
  // Track last-adjusted place index per slider (persists across drags for smooth alternating)
  const [lastAdjusted, setLastAdjusted] = useState<Record<number, number>>({});

  /** Update a place's percentage and redistribute to places below.
   *  Uses bottom-up alternating pattern (matching old highcall):
   *  Start from lowest place, move up, wrap to bottom again.
   *  Remembers position across incremental slider drags. */
  const updatePlacePercentage = useCallback(
    (placeIdx: number, newPct: number) => {
      const rounded = Math.max(0, Math.round(newPct));
      const newPlaces = config.places.map((p) => ({ ...p }));
      const currentPct = newPlaces[placeIdx].percentage;
      const diff = rounded - currentPct;
      if (diff === 0) return;

      const isLast = placeIdx === newPlaces.length - 1;

      if (isLast) {
        // Last place: borrow from place directly above
        const aboveIdx = placeIdx - 1;
        if (aboveIdx < 0) return;
        if (diff > 0 && newPlaces[aboveIdx].percentage < diff) return;
        newPlaces[placeIdx].percentage = rounded;
        newPlaces[aboveIdx].percentage -= diff;
      } else {
        // Non-last: redistribute across places below with alternating bottom-up pattern
        const belowCount = newPlaces.length - placeIdx - 1;
        const totalBelow = newPlaces.slice(placeIdx + 1).reduce((s, p) => s + p.percentage, 0);

        if (diff > 0 && diff > totalBelow) return;

        newPlaces[placeIdx].percentage = rounded;

        // Start from last-remembered position, or bottom
        let cursor = lastAdjusted[placeIdx] ?? (belowCount - 1);
        let remaining = Math.abs(diff);
        let safetyCounter = 0;
        const maxIter = remaining * belowCount + belowCount; // prevent infinite loops

        while (remaining > 0 && safetyCounter < maxIter) {
          safetyCounter++;
          const actualIdx = placeIdx + 1 + cursor;

          if (diff > 0) {
            // Taking from below
            if (newPlaces[actualIdx].percentage > 0) {
              newPlaces[actualIdx].percentage -= 1;
              remaining--;
            }
          } else {
            // Giving to below
            newPlaces[actualIdx].percentage += 1;
            remaining--;
          }

          // Move up, wrap to bottom
          cursor--;
          if (cursor < 0) cursor = belowCount - 1;
        }

        // Remember where we stopped for next incremental drag
        setLastAdjusted((prev) => ({ ...prev, [placeIdx]: cursor }));
      }

      // Ensure exactly 100%
      const sum = newPlaces.reduce((s, p) => s + p.percentage, 0);
      if (sum !== 100) {
        const lastIdx = newPlaces.length - 1;
        newPlaces[lastIdx].percentage = Math.max(0, newPlaces[lastIdx].percentage + (100 - sum));
      }

      // Recalc amounts
      newPlaces.forEach((p) => {
        p.prize_amount =
          config.round_prize_total > 0
            ? Math.round((config.round_prize_total * p.percentage) / 100)
            : 0;
      });

      onChange({ ...config, places: newPlaces });
    },
    [config, onChange],
  );

  const handlePrizeAmountChange = (total: number) => {
    const newPlaces = config.places.map((p) => ({
      ...p,
      prize_amount: total > 0 ? Math.round((total * p.percentage) / 100) : 0,
    }));
    onChange({ ...config, round_prize_total: total, places: newPlaces });
  };

  const handlePlacesCountChange = (count: number) => {
    const newPlaces = createDefaultPlaces(count, config.round_prize_total);
    onChange({ ...config, places: newPlaces });
  };

  // Round options: 1..totalRounds — exclude already-used rounds (except current).
  // AVG (round 0) is a scoring-only construct and is not selectable in setup;
  // legacy entries with round_number === 0 keep showing "AVG" via the trigger
  // but are no longer offered as a switchable option.
  const roundOptions = Array.from({ length: totalRounds }, (_, i) => i + 1).filter(
    (r) => r === config.round_number || !usedRounds.includes(r),
  );

  const sliderGradient = (pct: number) => {
    const color = isComplete ? '#3b82f6' : '#f59e0b';
    return `linear-gradient(to right, ${color} 0%, ${color} ${pct}%, #e5e7eb ${pct}%, #e5e7eb 100%)`;
  };

  // Summary for collapsed header
  const placesSummary = config.places
    .map((p) => `${PLACE_LABELS[p.place - 1] ?? `${p.place}th`}: ${p.percentage}%`)
    .join(', ');

  return (
    <div className="rounded-lg border bg-card overflow-hidden">
      {/* Collapsible header — always visible */}
      <div
        className="flex items-center gap-2 px-4 py-3 cursor-pointer select-none hover:bg-muted/40 transition-colors"
        onClick={() => setCollapsed((v) => !v)}
      >
        {collapsed ? (
          <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" />
        ) : (
          <ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground" />
        )}
        <div className="flex-1 min-w-0">
          <span className="text-sm font-medium">
            {formatRoundDisplay(config.round_number)}
          </span>
          {config.round_prize_total > 0 && (
            <span className="text-sm text-muted-foreground ml-2">
              • {formatCurrency(config.round_prize_total)}
            </span>
          )}
          {collapsed && (
            <span className="text-xs text-muted-foreground ml-2">
              • {placesSummary}
            </span>
          )}
        </div>
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onRemove();
          }}
          className="p-1.5 rounded-md text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition-colors shrink-0"
          title="Remove prize"
        >
          <Trash2 className="h-3.5 w-3.5" />
        </button>
      </div>

      {/* Expandable body */}
      {!collapsed && (
        <div className="px-4 pb-4 space-y-4 border-t">
          {/* Config row: For dropdown, Prize Amount, Places */}
          <div className="grid grid-cols-[1fr_1fr_auto] gap-3 items-end pt-3">
            <div className="space-y-1">
              <Label className="text-xs">For</Label>
              <Select
                value={String(config.round_number)}
                onValueChange={(v) => onChange({ ...config, round_number: Number(v) })}
              >
                <SelectTrigger className="h-9 text-sm">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {roundOptions.map((r) => (
                    <SelectItem key={r} value={String(r)}>
                      {formatRoundDisplay(r)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1">
              <Label className="text-xs">Prize Amount</Label>
              <CurrencyInput
                value={config.round_prize_total === 0 ? '' : String(config.round_prize_total)}
                onChange={(v) => {
                  const n = parseFloat(v);
                  handlePrizeAmountChange(Number.isNaN(n) ? 0 : n);
                }}
                placeholder="0"
                className="h-9 text-sm"
              />
            </div>

            <div className="space-y-1">
              <Label className="text-xs">Places</Label>
              <Select
                value={String(config.places.length)}
                onValueChange={(v) => handlePlacesCountChange(Number(v))}
              >
                <SelectTrigger className="h-9 text-sm w-20">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Array.from({ length: 10 }, (_, i) => i + 1).map((n) => (
                    <SelectItem key={n} value={String(n)}>
                      {n}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* All places with sliders */}
          <div className="space-y-3">
            {config.places.map((place, idx) => (
              <div key={idx} className="rounded-md border bg-muted/20 p-3 space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium">
                    {PLACE_LABELS[idx] ?? `${idx + 1}th`} Place
                  </span>
                  <span className="text-sm font-semibold text-foreground">
                    {config.round_prize_total > 0
                      ? formatCurrency(place.prize_amount)
                      : `${place.percentage}%`}
                  </span>
                </div>

                {/* Percentage input + slider side by side */}
                <div className="grid grid-cols-[100px_1fr] gap-3 items-center">
                  <div className="relative">
                    <Input
                      type="number"
                      min={0}
                      max={100}
                      step={1}
                      value={place.percentage}
                      onChange={(e) => {
                        const v = e.target.value === '' ? 0 : Number(e.target.value);
                        updatePlacePercentage(idx, v);
                      }}
                      className="h-8 text-xs pr-6"
                    />
                    <span className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-muted-foreground">
                      %
                    </span>
                  </div>

                  <div className="space-y-0.5">
                    <input
                      type="range"
                      min={0}
                      max={100}
                      step={1}
                      value={place.percentage}
                      onChange={(e) => updatePlacePercentage(idx, Number(e.target.value))}
                      className="w-full h-2 rounded-lg appearance-none cursor-pointer [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-4 [&::-webkit-slider-thumb]:h-4 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-white [&::-webkit-slider-thumb]:border-2 [&::-webkit-slider-thumb]:border-gray-400 [&::-webkit-slider-thumb]:shadow"
                      style={{ background: sliderGradient(place.percentage) }}
                    />
                    <div className="flex justify-between text-[10px] text-muted-foreground">
                      <span>0%</span>
                      <span className="font-medium text-blue-600 dark:text-blue-400">
                        {place.percentage}%
                      </span>
                      <span>100%</span>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Total footer */}
          <div
            className={`rounded px-3 py-1.5 text-xs font-medium flex justify-between ${
              isComplete
                ? 'bg-green-50 text-green-800 dark:bg-green-950/30 dark:text-green-400'
                : 'bg-amber-50 text-amber-800 dark:bg-amber-950/30 dark:text-amber-400'
            }`}
          >
            <span>
              Total: {totalPct}%
              {config.round_prize_total > 0 && (
                <> ({formatCurrency(config.places.reduce((s, p) => s + p.prize_amount, 0))})</>
              )}
            </span>
            {!isComplete && <span>{100 - totalPct}% remaining</span>}
          </div>
        </div>
      )}
    </div>
  );
}

const SECTIONS = ['Event Details', 'Handicap & Rounds', 'Prizes'] as const;

/**
 * Per-event judge-sheet template override. NULL = use the auto-classified
 * default; a non-null id pins the event to that specific row in
 * public.judge_sheet_templates. System templates and custom ones live in
 * the same select; the admin sees what they can pick from the API.
 */
function JudgeSheetTemplateField({
  value,
  onChange,
}: {
  value: string | null;
  onChange: (v: string | null) => void;
}) {
  const { data: templates, isLoading } = useJudgeSheetTemplates();
  // Sentinel value because the underlying Select control rejects the
  // empty string as an item value.
  const AUTO = '__auto__';
  return (
    <div className="space-y-1.5">
      <Label htmlFor="judge_sheet_template_id">Judge's Sheet Template</Label>
      <Select
        value={value ?? AUTO}
        onValueChange={(v) => onChange(v === AUTO ? null : v)}
      >
        <SelectTrigger id="judge_sheet_template_id" className="w-full md:w-96">
          <SelectValue placeholder={isLoading ? 'Loading…' : 'Auto (by event type)'} />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value={AUTO}>Auto (by event type)</SelectItem>
          {(templates ?? []).map((t) => (
            <SelectItem key={t.id} value={t.id}>
              {t.display_name}
              {t.is_system ? ' · System' : ''}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <p className="text-xs text-muted-foreground">
        Controls the column layout printed on this event's judge sheet.
        Manage templates in Settings → System Configuration.
      </p>
    </div>
  );
}

export function EditEventDialog({
  open,
  onOpenChange,
  event,
  onSave,
}: EditEventDialogProps) {
  const [form, setForm] = useState<EventFormData>(formDefaults);
  const [hasPickAndDraw, setHasPickAndDraw] = useState(false);
  // Dynamic prize entries (not pre-built for all rounds)
  const [prizes, setPrizes] = useState<RoundPrizeConfig[]>([]);
  // Prize adjustments state
  const [prizeAdjustments, setPrizeAdjustments] = useState<PrizeAdjustment[]>([]);
  const [showAdjustmentForm, setShowAdjustmentForm] = useState(false);
  const [adjustmentForm, setAdjustmentForm] = useState({
    operation: 'add' as 'add' | 'subtract',
    adjustment_type: 'amount' as 'amount' | 'percentage',
    amount: '',
    percentage: '',
    reason: '',
  });

  // Step navigation state
  const [openSection, setOpenSection] = useState(0);
  const [expandAll, setExpandAll] = useState(false);

  // Stripe fee config — drives the live transaction-fee preview rendered
  // under the entry-fee input. The preview is a derivation of the fee
  // config and the current entry_fee form value; the backend re-derives
  // the same number at registration time so what the organizer sees is
  // what the registrant will be charged.
  const { data: stripeFeeConfig } = useStripeFeeConfig();
  const isAllOrgAdmin = useAuthStore((s) => s.isAllOrgAdmin)();
  const [feeConfigOpen, setFeeConfigOpen] = useState(false);
  const liveTxnFee = stripeFeeConfig
    ? calculateTransactionFee(form.entry_fee ?? 0, stripeFeeConfig)
    : 0;

  useEffect(() => {
    if (open) {
      const merged = event ? { ...formDefaults, ...event } : { ...formDefaults };
      // Treat a stored max_contestants of 0 the same as "unlimited" (empty
      // input). The DB column's semantics are positive-int = hard cap, and
      // 0 is interpreted by the registration capacity check as "always
      // full". Showing "0" in the input would let the user re-save it
      // unchanged and silently block every signup.
      if (merged.max_contestants != null && merged.max_contestants <= 0) {
        merged.max_contestants = undefined;
      }
      setForm(merged);
      setHasPickAndDraw(
        (merged.pick_count != null && merged.pick_count > 0) ||
          (merged.draw_count != null && merged.draw_count > 0),
      );
      // Load existing prizes (only ones that were actually added)
      const existing = (merged.round_prizes as RoundPrizeConfig[] | undefined) ?? [];
      setPrizes(existing.filter((r) => r.has_prizes));
      // Load existing prize adjustments
      setPrizeAdjustments((merged.prize_adjustments as PrizeAdjustment[]) ?? []);
      setShowAdjustmentForm(false);
      setAdjustmentForm({ operation: 'add', adjustment_type: 'amount', amount: '', percentage: '', reason: '' });
      // Reset stepper
      setOpenSection(0);
      setExpandAll(false);
    }
  }, [open, event]);

  // Auto-set guaranteed_runs when progressive_after_round changes
  useEffect(() => {
    if (form.progressive_after_round && form.progressive_after_round > 0) {
      setForm((prev) => ({
        ...prev,
        guaranteed_runs: form.progressive_after_round,
      }));
    }
  }, [form.progressive_after_round]);

  const set = (field: string, value: unknown) =>
    setForm((prev) => ({ ...prev, [field]: value }));

  // Calculate total adjustments
  const activeAdjustments = prizeAdjustments.filter((a) => !a.markedForDeletion);
  const totalAdjustments = activeAdjustments.reduce((sum, adj) => {
    if (adj.adjustment_type === 'amount') {
      return sum + (adj.operation === 'add' ? adj.amount : -adj.amount);
    } else {
      const base = form.total_prize_amount ?? 0;
      const pctAmount = (adj.percentage / 100) * base;
      return sum + (adj.operation === 'add' ? pctAmount : -pctAmount);
    }
  }, 0);
  const availablePrize = (form.total_prize_amount ?? 0) + totalAdjustments;

  const saveAdjustment = () => {
    const isAmount = adjustmentForm.adjustment_type === 'amount';
    const val = isAmount ? parseFloat(adjustmentForm.amount) : parseFloat(adjustmentForm.percentage);
    if (isNaN(val) || val <= 0 || !adjustmentForm.reason.trim()) return;

    const newAdj: PrizeAdjustment = {
      id: `temp-${Date.now()}`,
      operation: adjustmentForm.operation,
      adjustment_type: adjustmentForm.adjustment_type,
      amount: isAmount ? val : 0,
      percentage: !isAmount ? val : 0,
      reason: adjustmentForm.reason.trim(),
    };
    setPrizeAdjustments((prev) => [...prev, newAdj]);
    setAdjustmentForm({ operation: 'add', adjustment_type: 'amount', amount: '', percentage: '', reason: '' });
    setShowAdjustmentForm(false);
  };

  const removeAdjustment = (id: string) => {
    if (id.startsWith('temp-')) {
      setPrizeAdjustments((prev) => prev.filter((a) => a.id !== id));
    } else {
      setPrizeAdjustments((prev) =>
        prev.map((a) => (a.id === id ? { ...a, markedForDeletion: true } : a)),
      );
    }
  };

  const handleSave = () => {
    const data: EventFormData = {
      ...form,
      // Custom name is optional — normalise empty/whitespace to '' so the
      // serializer downstream can treat it as "use the event type as title".
      event_name: form.event_name.trim(),
      round_prizes: prizes.length > 0 ? prizes.map((p) => ({ ...p, has_prizes: true })) : undefined,
      prize_adjustments: activeAdjustments.length > 0 ? activeAdjustments : undefined,
    };
    onSave(data);
    onOpenChange(false);
  };

  const isNew = !event?.id;

  // Derived max teams
  const maxTeams =
    form.max_contestants != null ? Math.floor(form.max_contestants / 2) : undefined;

  const handleMaxTeamsChange = (teams: number | undefined) => {
    if (teams == null) {
      set('max_contestants', undefined);
    } else {
      set('max_contestants', teams * 2);
    }
  };

  const handleMaxContestantsChange = (contestants: number | undefined) => {
    set('max_contestants', contestants);
  };

  // Auto prize calc
  const autoPrize = (form.max_contestants ?? 0) * (form.entry_fee ?? 0);

  const slideActive = form.slide_type != null && form.slide_type !== 'NONE';

  const totalRounds = form.total_rounds ?? 1;

  // Prize entry management
  const usedRounds = prizes.map((p) => p.round_number);

  const addPrize = () => {
    // Find first unused round (1, 2, ...). AVG (round 0) is scoring-only and
    // is no longer offered in setup, so new prizes always seed to a numbered
    // round — defaulting to 1 if every numbered round is already used.
    const allOptions = Array.from({ length: totalRounds }, (_, i) => i + 1);
    const nextRound = allOptions.find((r) => !usedRounds.includes(r)) ?? 1;

    // Split available prize evenly across all prizes (existing + new)
    const newCount = prizes.length + 1;
    const splitAmount = availablePrize > 0 ? Math.floor(availablePrize / newCount) : 0;

    const newPrize: RoundPrizeConfig = {
      round_number: nextRound,
      has_prizes: true,
      round_prize_total: splitAmount,
      places: createDefaultPlaces(3, splitAmount),
    };

    // Re-split existing prizes to the same even amount
    setPrizes((prev) => [
      ...prev.map((p) => ({
        ...p,
        round_prize_total: splitAmount,
        places: p.places.map((pl) => ({
          ...pl,
          prize_amount: splitAmount > 0 ? Math.round((splitAmount * pl.percentage) / 100) : 0,
        })),
      })),
      newPrize,
    ]);
  };

  const updatePrize = useCallback(
    (idx: number, updated: RoundPrizeConfig) => {
      setPrizes((prev) => {
        const next = [...prev];
        next[idx] = updated;
        return next;
      });
    },
    [],
  );

  const removePrize = (idx: number) => {
    setPrizes((prev) => prev.filter((_, i) => i !== idx));
  };

  const totalAllocated = prizes.reduce((s, p) => s + p.round_prize_total, 0);

  /* ── Step content sections ── */

  const stepEventDetails = (
    <div className="space-y-6">
      {/* ─── Event Details ─── */}
      <div className="space-y-4">
        <SectionTitle>Event Details</SectionTitle>

        {/* Event Name + Event Type */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-1.5">
            <Label htmlFor="event_name">
              Event Name <span className="text-muted-foreground text-xs">(optional)</span>
            </Label>
            <Input
              id="event_name"
              value={form.event_name}
              onChange={(e) => set('event_name', e.target.value)}
              placeholder={form.event_type || 'Defaults to event type'}
            />
            <p className="text-xs text-muted-foreground">
              Leave blank to use the event type as the title.
            </p>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="event_type">Event Type</Label>
            <Select
              value={form.event_type ?? 'Team Roping'}
              onValueChange={(v) => set('event_type', v)}
            >
              <SelectTrigger id="event_type">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {EVENT_TYPE_OPTIONS.map((t) => (
                  <SelectItem key={t} value={t}>
                    {t}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Judge sheet template override — defaults to auto-classify from
            discipline name; admin can pick any DB-configured layout. */}
        <JudgeSheetTemplateField
          value={(form.judge_sheet_template_id as string | null | undefined) ?? null}
          onChange={(v) => set('judge_sheet_template_id', v)}
        />

        {/* Max Teams + Max Contestants + Contestants/Perf + Entry Fee */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="space-y-1.5">
            <Label htmlFor="max_teams">Max Teams</Label>
            <Input
              id="max_teams"
              type="number"
              min={1}
              value={maxTeams ?? ''}
              onChange={(e) => handleMaxTeamsChange(parseIntOpt(e.target.value))}
              placeholder="Unlimited"
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="max_contestants">Max Contestants</Label>
            <Input
              id="max_contestants"
              type="number"
              min={1}
              value={form.max_contestants ?? ''}
              onChange={(e) => handleMaxContestantsChange(parseIntOpt(e.target.value))}
              placeholder="Unlimited"
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="contestants_per_performance">Contestants per Performance</Label>
            <Input
              id="contestants_per_performance"
              type="number"
              min={1}
              value={form.contestants_per_performance ?? ''}
              onChange={(e) =>
                set('contestants_per_performance', parseIntOpt(e.target.value))
              }
              placeholder="Auto"
            />
            <p className="text-xs text-muted-foreground">
              Default size of each performance. Slack is uncapped — any overflow rolls into slack.
            </p>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="entry_fee">Entry Fee</Label>
            <CurrencyInput
              id="entry_fee"
              value={form.entry_fee != null ? String(form.entry_fee) : ''}
              onChange={(v) => {
                const n = parseFloat(v);
                set('entry_fee', Number.isNaN(n) ? undefined : n);
              }}
              placeholder="0"
            />
            {stripeFeeConfig && (
              <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                <span>
                  Transaction fee:{' '}
                  <span className="font-medium text-foreground">
                    {formatCurrency(liveTxnFee)}
                  </span>
                  {stripeFeeConfig.enabled ? (
                    <span className="ml-1 text-muted-foreground/80">
                      ({(stripeFeeConfig.percentage_rate * 100).toFixed(2).replace(/\.?0+$/, '')}%
                      {' + '}
                      {formatCurrency(stripeFeeConfig.fixed_fee_cents / 100)} Stripe)
                    </span>
                  ) : (
                    <span className="ml-1 text-muted-foreground/80">(disabled)</span>
                  )}
                </span>
                {isAllOrgAdmin && (
                  <button
                    type="button"
                    onClick={() => setFeeConfigOpen(true)}
                    className="p-0.5 rounded hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"
                    title="Edit Stripe fee config (super admin)"
                    aria-label="Edit Stripe fee config"
                  >
                    <Pencil className="h-3 w-3" />
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ─── Event Options (merged from Event Configuration) ─── */}
      <div className="space-y-4">
        <h4 className="text-sm font-semibold text-foreground border-t pt-4">Event Options</h4>

        {/* Checkboxes row 1 */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <InlineCheckbox
            label="Century Jackpot (Combined age \u2265 100)"
            checked={!!form.is_century_jackpot}
            onChange={(v) => set('is_century_jackpot', v)}
          />
          <InlineCheckbox
            label="Mixed Gender (One male, one female)"
            checked={!!form.is_mixed_gender}
            onChange={(v) => set('is_mixed_gender', v)}
          />
        </div>

        {/* Checkboxes row 2 */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <InlineCheckbox
            label="Allow entry without picked partners"
            checked={form.allow_no_partner_entry ?? false}
            onChange={(v) => set('allow_no_partner_entry', v)}
          />
          <InlineCheckbox
            label="Require mutual confirmation for picks"
            checked={form.enforce_mutual_picks ?? true}
            onChange={(v) => set('enforce_mutual_picks', v)}
          />
        </div>

        {/* Multi-registration cap. Off by default (one entry per
            contestant); when on, the cap below is enforced server-side
            via MAX_REGISTRATIONS_REACHED. */}
        <div className="space-y-2">
          <InlineCheckbox
            label="Allow same contestant to enter multiple times"
            checked={form.allow_multiple_registrations ?? false}
            onChange={(v) => set('allow_multiple_registrations', v)}
          />
          {form.allow_multiple_registrations && (
            <div className="ml-6 space-y-1.5 max-w-[220px]">
              <Label htmlFor="max_per_user" className="text-xs">
                Max entries per contestant
              </Label>
              <Input
                id="max_per_user"
                type="number"
                min={1}
                value={form.max_registrations_per_user ?? ''}
                onChange={(e) =>
                  set('max_registrations_per_user', parseIntOpt(e.target.value))
                }
                placeholder="1"
              />
              <p className="text-xs text-muted-foreground">
                Defaults to 1 if left blank — set higher when a contestant may
                hold multiple entries (e.g. enter the same horse twice).
              </p>
            </div>
          )}
          {/* Balanced Header/Heeler split. Only meaningful on team roping
              with a cap above 1 — the backend ignores the flag otherwise,
              so we hide it rather than offering a setting that does
              nothing. */}
          {form.allow_multiple_registrations &&
            (form.max_registrations_per_user ?? 1) > 1 &&
            isTeamRopingEvent({ trig_event_type_name: form.event_type }) && (
              <div className="ml-6 space-y-1.5">
                <InlineCheckbox
                  label="Require an even Header / Heeler split"
                  checked={form.enforce_balanced_positions ?? false}
                  onChange={(v) => set('enforce_balanced_positions', v)}
                />
                <p className="text-xs text-muted-foreground">
                  Contestants must spread their entries across both ends —{' '}
                  {describeBalancedSplit(form.max_registrations_per_user ?? 1)}.
                  Without this a contestant can use every entry on the same
                  side. Existing entries are never changed; the rule applies to
                  new entries only.
                </p>
              </div>
            )}
        </div>

        {/* Pick and Draw */}
        <div className="space-y-3">
          <InlineCheckbox
            label="Pick and Draw (Enable pick and draw rounds)"
            checked={hasPickAndDraw}
            onChange={(v) => {
              setHasPickAndDraw(v);
              if (!v) {
                setForm((prev) => ({
                  ...prev,
                  pick_count: undefined,
                  draw_count: undefined,
                }));
              }
            }}
          />

          {hasPickAndDraw && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 ml-6">
              <div className="space-y-1">
                <Label className="text-xs">Pick Rounds</Label>
                <Input
                  type="number"
                  min={0}
                  max={10}
                  value={form.pick_count ?? ''}
                  onChange={(e) => set('pick_count', parseIntOpt(e.target.value))}
                  placeholder="0"
                  className="h-8 text-sm"
                />
                <p className="text-xs text-muted-foreground">
                  Number of rounds where contestants pick partners
                </p>
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Draw Rounds</Label>
                <Input
                  type="number"
                  min={0}
                  max={10}
                  value={form.draw_count ?? ''}
                  onChange={(e) => set('draw_count', parseIntOpt(e.target.value))}
                  placeholder="0"
                  className="h-8 text-sm"
                />
                <p className="text-xs text-muted-foreground">
                  Number of rounds with random draw
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Slide System */}
        <div className="space-y-3 mt-2">
          <SubSectionTitle>Slide System</SubSectionTitle>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="space-y-1">
              <Label className="text-xs">Slide Type</Label>
              <Select
                value={form.slide_type || 'NONE'}
                onValueChange={(v) => set('slide_type', v)}
              >
                <SelectTrigger className="h-9 text-sm">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {SLIDE_TYPES.map((t) => (
                    <SelectItem key={t.value} value={t.value}>
                      {t.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                {form.slide_type === 'SLIDE_DOWN'
                  ? 'Teams above get penalties, teams below get bonuses'
                  : form.slide_type === 'SLIDE_UP_DOWN'
                    ? 'Teams above/below base get penalties/bonuses'
                    : 'No slide adjustments'}
              </p>
            </div>
            {slideActive && (
              <>
                <div className="space-y-1">
                  <Label className="text-xs">Base Handicap</Label>
                  <Input
                    type="number"
                    min={1}
                    max={15}
                    step={0.5}
                    value={form.slide_base_handicap ?? ''}
                    onChange={(e) =>
                      set('slide_base_handicap', parseNum(e.target.value))
                    }
                    placeholder="1-15"
                    className="h-9 text-sm"
                  />
                  <p className="text-xs text-muted-foreground">
                    Base for slide calculations (1-15, 0.5 increments)
                  </p>
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Seconds per 0.5 Handicap</Label>
                  <Input
                    type="number"
                    min={0.5}
                    max={5}
                    step={0.5}
                    value={form.slide_conversion_rate ?? ''}
                    onChange={(e) =>
                      set('slide_conversion_rate', parseNum(e.target.value))
                    }
                    placeholder="0.5"
                    className="h-9 text-sm"
                  />
                  <p className="text-xs text-muted-foreground">
                    Time adjustment per 0.5 handicap difference
                  </p>
                </div>
              </>
            )}
          </div>

          {/* Slide Preview */}
          {slideActive && form.slide_base_handicap != null && (
            <div className="rounded-md bg-blue-50 border border-blue-200 p-3 text-sm dark:bg-blue-950/30 dark:border-blue-800">
              <p className="font-medium text-blue-900 dark:text-blue-300 mb-2">
                Example Calculations:
              </p>
              <div className="space-y-1 text-blue-700 dark:text-blue-400">
                <p>
                  Team handicap {form.slide_base_handicap + 2}: +
                  {(2 * 2 * (form.slide_conversion_rate ?? 0.5)).toFixed(1)}s penalty
                  (above base)
                </p>
                <p>Team handicap {form.slide_base_handicap}: No adjustment</p>
                <p>
                  Team handicap {form.slide_base_handicap - 2}: -
                  {(2 * 2 * (form.slide_conversion_rate ?? 0.5)).toFixed(1)}s bonus
                  (below base)
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );

  const stepHandicapRounds = (
    <div className="space-y-6">
      {/* ─── Handicap Limits ─── */}
      <div className="space-y-4">
        <SectionTitle>Handicap Limits</SectionTitle>
        <p className="text-xs text-muted-foreground">
          Set maximum handicap values. Leave empty for unlimited handicaps.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="space-y-1.5">
            <Label>Handicap Type</Label>
            <Select
              value={form.handicap_type || 'combined'}
              onValueChange={(v) => set('handicap_type', v)}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {HANDICAP_TYPES.map((ht) => (
                  <SelectItem key={ht.value} value={ht.value}>
                    {ht.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="max_combined">Max Combined Handicap</Label>
            <Input
              id="max_combined"
              type="number"
              min={0}
              max={20}
              step={0.5}
              value={form.max_combined_handicap ?? ''}
              onChange={(e) => set('max_combined_handicap', parseNum(e.target.value))}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="max_header">Max Header Handicap</Label>
            <Input
              id="max_header"
              type="number"
              min={0}
              max={20}
              step={0.5}
              value={form.header_max_handicap ?? ''}
              onChange={(e) => set('header_max_handicap', parseNum(e.target.value))}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="max_heeler">Max Heeler Handicap</Label>
            <Input
              id="max_heeler"
              type="number"
              min={0}
              max={20}
              step={0.5}
              value={form.heeler_max_handicap ?? ''}
              onChange={(e) => set('heeler_max_handicap', parseNum(e.target.value))}
            />
          </div>
        </div>
        <p className="text-xs text-muted-foreground">
          Values must be between 0-20 in increments of 0.5
        </p>
      </div>

      {/* ─── Rounds Configuration ─── */}
      <div className="space-y-4">
        <h4 className="text-sm font-semibold text-foreground border-t pt-4">Rounds Configuration</h4>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-1.5">
            <Label>Number of Rounds</Label>
            <Select
              value={String(form.total_rounds ?? 1)}
              onValueChange={(v) => set('total_rounds', Number(v))}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {ROUND_OPTIONS.map((n) => (
                  <SelectItem key={n} value={String(n)}>
                    {n}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-1.5">
            <Label>Progressive After Round</Label>
            <Select
              value={form.progressive_after_round != null ? String(form.progressive_after_round) : 'none'}
              onValueChange={(v) => set('progressive_after_round', v === 'none' ? undefined : Number(v))}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">None</SelectItem>
                {Array.from({ length: totalRounds - 1 }, (_, i) => i + 1).map((num) => (
                  <SelectItem key={num} value={String(num)}>
                    {formatRoundDisplay(num)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Guaranteed Runs */}
        <div className="space-y-1.5">
          <Label>Guaranteed Runs</Label>
          <Select
            value={String(form.guaranteed_runs ?? 1)}
            onValueChange={(v) => set('guaranteed_runs', Number(v))}
          >
            <SelectTrigger className="w-full md:w-64">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {Array.from({ length: totalRounds }, (_, i) => i + 1).map((num) => (
                <SelectItem key={num} value={String(num)}>
                  {num} {num === 1 ? 'run' : 'runs'}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <p className="text-xs text-muted-foreground">
            Teams will get at least this many runs before being eliminated for no-time
          </p>
        </div>
      </div>
    </div>
  );

  const stepPrizes = (
    <div className="space-y-4">
      <SectionTitle>Prize Management</SectionTitle>

      {/* Total Prize Amount + Available Prize Amount */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-1.5">
          <Label>Total Prize Amount</Label>
          <CurrencyInput
            value={form.total_prize_amount != null ? String(form.total_prize_amount) : ''}
            onChange={(v) => set('total_prize_amount', parseNum(v))}
            placeholder="0"
          />
          <p className="text-xs text-muted-foreground">
            Base prize amount for this event (before adjustments)
          </p>
          {autoPrize > 0 && form.total_prize_amount !== autoPrize && (
            <button
              type="button"
              className="text-xs text-blue-600 hover:underline dark:text-blue-400"
              onClick={() => set('total_prize_amount', autoPrize)}
            >
              Auto-calc: {formatCurrency(autoPrize)} ({form.max_contestants} x{' '}
              {formatCurrency(form.entry_fee ?? 0)})
            </button>
          )}
        </div>

        {/* Available Prize Amount — green dashed panel */}
        <div className="rounded-lg border-2 border-dashed border-emerald-400 bg-emerald-50 dark:bg-emerald-950/30 dark:border-emerald-600 p-4 flex flex-col justify-center">
          <p className="text-sm font-medium text-emerald-700 dark:text-emerald-400">
            Available Prize Amount
          </p>
          <p className="text-2xl font-bold text-emerald-800 dark:text-emerald-300">
            {formatCurrency(availablePrize)}
          </p>
          <p className="text-xs">
            <span className="text-emerald-600 dark:text-emerald-500">Base: {formatCurrency(form.total_prize_amount ?? 0)}</span>
            {' | '}
            <span className={totalAdjustments >= 0 ? 'text-emerald-600 dark:text-emerald-500' : 'text-red-600 dark:text-red-400'}>
              Adjustments: {totalAdjustments >= 0 ? '+' : ''}{formatCurrency(totalAdjustments)}
            </span>
          </p>
        </div>
      </div>

      {/* ─── Prize Adjustments ─── */}
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <SubSectionTitle>Prize Adjustments</SubSectionTitle>
          {!showAdjustmentForm && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => setShowAdjustmentForm(true)}
              className="h-7 text-xs"
            >
              <Plus className="h-3 w-3 mr-1" />
              Add Adjustment
            </Button>
          )}
        </div>

        {/* Adjustment form */}
        {showAdjustmentForm && (
          <div className="rounded-lg border bg-muted/30 p-4 space-y-3">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              {/* Operation */}
              <div className="space-y-1">
                <Label className="text-xs">Operation</Label>
                <Select
                  value={adjustmentForm.operation}
                  onValueChange={(v) =>
                    setAdjustmentForm((prev) => ({ ...prev, operation: v as 'add' | 'subtract' }))
                  }
                >
                  <SelectTrigger className="h-9 text-sm">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="add">Add Money (+)</SelectItem>
                    <SelectItem value="subtract">Subtract Money (-)</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Adjustment Type */}
              <div className="space-y-1">
                <Label className="text-xs">Adjustment Type</Label>
                <Select
                  value={adjustmentForm.adjustment_type}
                  onValueChange={(v) =>
                    setAdjustmentForm((prev) => ({ ...prev, adjustment_type: v as 'amount' | 'percentage' }))
                  }
                >
                  <SelectTrigger className="h-9 text-sm">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="amount">Fixed Amount</SelectItem>
                    <SelectItem value="percentage">Percentage</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Value */}
              <div className="space-y-1">
                <Label className="text-xs">
                  {adjustmentForm.adjustment_type === 'amount' ? 'Amount' : 'Percentage'}
                </Label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">
                    {adjustmentForm.adjustment_type === 'amount' ? '$' : '%'}
                  </span>
                  <Input
                    type="number"
                    min={0}
                    step={adjustmentForm.adjustment_type === 'amount' ? 1 : 0.1}
                    value={
                      adjustmentForm.adjustment_type === 'amount'
                        ? adjustmentForm.amount
                        : adjustmentForm.percentage
                    }
                    onChange={(e) => {
                      const field =
                        adjustmentForm.adjustment_type === 'amount' ? 'amount' : 'percentage';
                      setAdjustmentForm((prev) => ({ ...prev, [field]: e.target.value }));
                    }}
                    placeholder="0"
                    className="pl-7 h-9 text-sm"
                  />
                </div>
              </div>
            </div>

            {/* Description / Reason */}
            <div className="space-y-1">
              <Label className="text-xs">Description <span className="text-destructive">*</span></Label>
              <Input
                value={adjustmentForm.reason}
                onChange={(e) =>
                  setAdjustmentForm((prev) => ({ ...prev, reason: e.target.value }))
                }
                placeholder="e.g. Sponsor contribution, Arena fees..."
                className="h-9 text-sm"
              />
            </div>

            {/* Actions */}
            <div className="flex justify-end gap-2">
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => {
                  setShowAdjustmentForm(false);
                  setAdjustmentForm({ operation: 'add', adjustment_type: 'amount', amount: '', percentage: '', reason: '' });
                }}
              >
                Cancel
              </Button>
              <Button
                type="button"
                size="sm"
                onClick={saveAdjustment}
                disabled={
                  !adjustmentForm.reason.trim() ||
                  (adjustmentForm.adjustment_type === 'amount'
                    ? !adjustmentForm.amount || parseFloat(adjustmentForm.amount) <= 0
                    : !adjustmentForm.percentage || parseFloat(adjustmentForm.percentage) <= 0)
                }
              >
                Save Adjustment
              </Button>
            </div>
          </div>
        )}

        {/* Adjustments list */}
        {activeAdjustments.length > 0 ? (
          <div className="space-y-1.5">
            {activeAdjustments.map((adj) => {
              const isAdd = adj.operation === 'add';
              const displayValue =
                adj.adjustment_type === 'amount'
                  ? formatCurrency(adj.amount)
                  : `${adj.percentage}%`;
              return (
                <div
                  key={adj.id}
                  className={`flex items-center justify-between rounded-md border px-3 py-2 text-sm ${
                    isAdd
                      ? 'border-emerald-200 bg-emerald-50/50 dark:border-emerald-800 dark:bg-emerald-950/20'
                      : 'border-red-200 bg-red-50/50 dark:border-red-800 dark:bg-red-950/20'
                  }`}
                >
                  <div className="flex items-center gap-2">
                    <span
                      className={`font-mono font-semibold ${
                        isAdd
                          ? 'text-emerald-700 dark:text-emerald-400'
                          : 'text-red-700 dark:text-red-400'
                      }`}
                    >
                      {isAdd ? '+' : '-'}{displayValue}
                    </span>
                    <span className="text-muted-foreground">{adj.reason}</span>
                  </div>
                  <button
                    type="button"
                    onClick={() => removeAdjustment(adj.id)}
                    className="p-1 rounded hover:bg-destructive/10 text-muted-foreground hover:text-destructive transition-colors"
                    title="Remove adjustment"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </div>
              );
            })}
          </div>
        ) : (
          !showAdjustmentForm && (
            <p className="text-xs text-muted-foreground italic">No prize adjustments added</p>
          )
        )}
      </div>

      {/* ─── Prizes — dynamically added ─── */}
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <SubSectionTitle>Prizes</SubSectionTitle>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={addPrize}
            disabled={usedRounds.filter((r) => r > 0).length >= totalRounds} // all numbered rounds used
            className="h-7 text-xs"
          >
            <Plus className="h-3 w-3 mr-1" />
            Add Prize
          </Button>
        </div>

        {/* Prize entries */}
        {prizes.length > 0 ? (
          <div className="space-y-4">
            {prizes.map((prize, idx) => (
              <PrizeEntryPanel
                key={`${prize.round_number}-${idx}`}
                config={prize}
                totalRounds={totalRounds}
                usedRounds={usedRounds}
                onChange={(updated) => updatePrize(idx, updated)}
                onRemove={() => removePrize(idx)}
              />
            ))}
          </div>
        ) : (
          <div className="rounded-lg border-2 border-dashed border-muted-foreground/25 p-6 text-center">
            <p className="text-sm text-muted-foreground">No prizes added yet</p>
            <button
              type="button"
              onClick={addPrize}
              className="mt-2 inline-flex items-center gap-1 text-sm text-blue-600 hover:underline dark:text-blue-400"
            >
              <Plus className="h-3.5 w-3.5" />
              Add Your First Prize
            </button>
          </div>
        )}
      </div>

      {/* Total Prize Summary — teal=exact, orange=under, red=over */}
      {(() => {
        const remaining = availablePrize - totalAllocated;
        const isOver = remaining < 0;
        const isUnder = remaining > 0 && prizes.length > 0;
        // exact match (or no prizes yet)
        const isExact = !isOver && !isUnder;

        let borderColor: string, bgColor: string, titleColor: string, subtextColor: string;
        if (isOver) {
          borderColor = 'border-red-400 dark:border-red-600';
          bgColor = 'bg-red-50 dark:bg-red-950/30';
          titleColor = 'text-red-800 dark:text-red-300';
          subtextColor = 'text-red-600 dark:text-red-400';
        } else if (isUnder) {
          borderColor = 'border-amber-400 dark:border-amber-600';
          bgColor = 'bg-amber-50 dark:bg-amber-950/30';
          titleColor = 'text-amber-800 dark:text-amber-300';
          subtextColor = 'text-amber-600 dark:text-amber-400';
        } else {
          borderColor = 'border-teal-400 dark:border-teal-600';
          bgColor = 'bg-teal-50 dark:bg-teal-950/30';
          titleColor = 'text-teal-800 dark:text-teal-300';
          subtextColor = 'text-teal-600 dark:text-teal-400';
        }

        return (
          <div className={`rounded-lg border-2 border-dashed ${borderColor} ${bgColor} px-4 py-3 flex items-center justify-between`}>
            <div>
              <p className={`text-sm font-semibold ${titleColor}`}>
                Total Prize Rounds Allocated:
              </p>
              {prizes.length > 0 ? (
                <p className={`text-xs ${subtextColor}`}>
                  {prizes.length} prize{prizes.length !== 1 ? 's' : ''} configured —{' '}
                  {formatCurrency(availablePrize)} available for allocation
                </p>
              ) : (
                <p className={`text-xs ${subtextColor}`}>
                  No prize rounds specified — {formatCurrency(availablePrize)} available for allocation
                </p>
              )}
              {isOver && (
                <p className="text-xs font-semibold text-red-700 dark:text-red-400 mt-1">
                  Over-allocated by {formatCurrency(Math.abs(remaining))} — reduce prize amounts or remove a round
                </p>
              )}
              {isUnder && (
                <p className="text-xs font-semibold text-amber-700 dark:text-amber-400 mt-1">
                  {formatCurrency(remaining)} unallocated — increase prize amounts or add another round
                </p>
              )}
              {isExact && prizes.length > 0 && (
                <p className="text-xs font-semibold text-teal-700 dark:text-teal-400 mt-1">
                  Fully allocated
                </p>
              )}
            </div>
            <div className="text-right">
              <p className={`text-lg font-bold ${titleColor}`}>
                {formatCurrency(totalAllocated)}
              </p>
              <p className={`text-xs ${subtextColor}`}>
                ({isOver ? `-${formatCurrency(Math.abs(remaining))}` : formatCurrency(remaining)} remaining)
              </p>
            </div>
          </div>
        );
      })()}
    </div>
  );

  const steps = [stepEventDetails, stepHandicapRounds, stepPrizes];

  return (
    <>
      {isAllOrgAdmin && stripeFeeConfig && (
        <StripeFeeConfigModal
          open={feeConfigOpen}
          onOpenChange={setFeeConfigOpen}
          config={stripeFeeConfig}
        />
      )}
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent hideClose className="sm:max-w-[1100px] p-0 gap-0 h-[85vh] flex flex-col overflow-hidden">
        {/* Visually hidden title for accessibility */}
        <DialogTitle className="sr-only">{isNew ? 'Add Event' : 'Edit Event'}</DialogTitle>

        {/* Fixed Header */}
        <div className="border-b px-6 py-4 shrink-0 rounded-t-lg" style={{ background: 'hsl(var(--sidebar-background))', color: 'hsl(var(--sidebar-foreground))' }}>
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-bold">
              {isNew ? 'Add Event' : 'Edit Event'}
            </h2>
            <button
              onClick={() => onOpenChange(false)}
              className="p-2 rounded-lg transition-colors opacity-70 hover:opacity-100"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Body: sidebar + content */}
        <div className="flex-1 flex min-h-0">
          {/* Sidebar stepper */}
          <div className="w-56 shrink-0 border-r bg-muted/30 flex flex-col py-4">
            <nav className="flex-1 space-y-1 px-2">
              {SECTIONS.map((name, idx) => {
                const isActive = !expandAll && openSection === idx;
                return (
                  <button
                    key={idx}
                    type="button"
                    onClick={() => {
                      setOpenSection(idx);
                      setExpandAll(false);
                    }}
                    className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-md text-left text-sm transition-colors ${
                      isActive
                        ? 'bg-primary text-primary-foreground font-medium'
                        : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                    }`}
                  >
                    <span
                      className={`flex items-center justify-center w-6 h-6 rounded-full text-xs font-semibold shrink-0 ${
                        isActive
                          ? 'bg-primary-foreground text-primary'
                          : 'bg-muted-foreground/20 text-muted-foreground'
                      }`}
                    >
                      {idx + 1}
                    </span>
                    <span className="truncate">{name}</span>
                  </button>
                );
              })}
            </nav>

            {/* Show all / Single view toggle */}
            <div className="px-4 pt-3 border-t mt-2">
              <button
                type="button"
                onClick={() => setExpandAll((v) => !v)}
                className="text-xs text-primary hover:underline"
              >
                {expandAll ? 'Single view' : 'Show all'}
              </button>
            </div>
          </div>

          {/* Scrollable content */}
          <div className="flex-1 overflow-y-auto px-6 py-6">
            {expandAll ? (
              <div className="space-y-8">
                {steps.map((step, idx) => (
                  <div key={idx}>
                    {idx > 0 && <hr className="mb-8 border-border" />}
                    {step}
                  </div>
                ))}
              </div>
            ) : (
              steps[openSection]
            )}
          </div>
        </div>

        {/* Fixed Footer */}
        <div className="border-t bg-background px-6 py-4 shrink-0">
          <div className="flex items-center justify-between">
            {/* Left: Previous / Next (hidden in expand-all) */}
            <div className="flex gap-2">
              {!expandAll && (
                <>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setOpenSection((s) => Math.max(0, s - 1))}
                    disabled={openSection === 0}
                  >
                    Previous
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setOpenSection((s) => Math.min(SECTIONS.length - 1, s + 1))}
                    disabled={openSection === SECTIONS.length - 1}
                  >
                    Next
                  </Button>
                </>
              )}
            </div>

            {/* Right: Cancel / Save */}
            <div className="flex gap-3">
              <Button variant="outline" onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
              <Button onClick={handleSave}>
                {isNew ? 'Save Event' : 'Update Event'}
              </Button>
            </div>
          </div>
        </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
